import { RolesLabelMap } from '@/auth/roles';
import { Badge } from '@/components/ui/badge';

import { PrismaEnums } from '@airreading/common';
import { type UserRoleLike } from '@airreading/common/data';

import clsx from 'clsx';

const RolesWithSchools = [
  PrismaEnums.Role.student,
  PrismaEnums.Role.parent,
  PrismaEnums.Role.org_admin,
  PrismaEnums.Role.school_admin,
  PrismaEnums.Role.school_contact,
] as const;

const RolesBackgroundColorMap: Record<PrismaEnums.Role, string> = {
  [PrismaEnums.Role.student]: 'bg-blue-500',
  [PrismaEnums.Role.parent]: 'bg-purple-500',
  [PrismaEnums.Role.air_admin]: 'bg-red-500',
  [PrismaEnums.Role.air_limited_admin]: 'bg-pink-500',
  [PrismaEnums.Role.coach]: 'bg-yellow-500',
  [PrismaEnums.Role.org_admin]: 'bg-green-500',
  [PrismaEnums.Role.school_admin]: 'bg-green-500',
  [PrismaEnums.Role.school_contact]: 'bg-slate-500',
  [PrismaEnums.Role.edge_coach]: 'bg-amber-600',
};

const inactiveRoleColor = 'bg-gray-500';

export interface RoleBadgeProps {
  userRole: UserRoleLike;
}

export const RoleBadge = ({ userRole: { role, school, status } }: RoleBadgeProps) => {
  return (
    <Badge className={status === 'inactive' ? inactiveRoleColor : clsx(RolesBackgroundColorMap[role])}>
      {RolesLabelMap[role]}
      {!!RolesWithSchools.find((r) => r === role) && `:${school?.name}`}
      {status === 'inactive' ? ':inactive' : ''}
    </Badge>
  );
};

export interface RoleBadgeListProps {
  userRoles: UserRoleLike[];
  /** When true, do not show the Edge Tutors badge (e.g. in Teacher/Edge Tutors portals) */
  hideEdgeCoachTag?: boolean;
}

export const RoleBadgeList = ({ userRoles: roles, hideEdgeCoachTag }: RoleBadgeListProps) => {
  const filteredRoles = hideEdgeCoachTag ? roles.filter((r) => r.role !== 'edge_coach') : roles;
  const sortedRoles = filteredRoles
    .map<RoleObjectWithKey>((role) => ({ ...role, key: keyOfUserRole(role) }))
    .toSorted(byUserRole);

  return (
    <>
      {sortedRoles.map((userRole) => (
        <RoleBadge key={keyOfUserRole(userRole)} userRole={userRole} />
      ))}
    </>
  );
};

interface RoleObjectWithKey extends UserRoleLike {
  key: string;
}

function keyOfUserRole({ role, schoolId }: UserRoleLike): string {
  return role + (schoolId ?? '');
}

function byUserRole(a: RoleObjectWithKey, b: RoleObjectWithKey): number {
  return a.key.localeCompare(b.key);
}
